import ApiExtension from './api-extension'

/**
 * @typedef {Object} DemProps
 * @property {number} projectId
 * @property {string} projectIdentifier
 * @property {string} demType
 * @property {string} format
 * @property {string} fileFormat
 * @property {string} filename
 * @property {number} scale
 * @property {import('@turf/helpers').MultiPolygon|undefined} bounds
 * @property {number[][]|null} palette
 * @property {number|null|undefined} crs
 */

export default class Exports extends ApiExtension {
  async ortho({
    projectId,
    projectIdentifier,
    bands,
    scale,
    fileFormat,
    filename,
    outputBands,
    crs,
    bounds = undefined,
  }) {
    if (scale > 1 || fileFormat !== 'GTiff' || bounds || crs !== 4326) {
      const params = {
        bands: bands + 'a',
        scale,
        output_driver: fileFormat,
        bounds,
        crs,
        output_bands: outputBands + 'a',
      }

      const results = await this.handleExport(
        projectIdentifier,
        'exports/scale',
        params,
        filename
      )
      return results[0]
    } else {
      return this.apiBase.getProjectResultUrl(projectId, 'ortho.tiff', filename)
    }
  }

  /**
   *
   * @param {DemProps} props
   * @returns
   */
  async dem({
    projectId,
    projectIdentifier,
    demType,
    format,
    fileFormat,
    filename,
    bounds = undefined,
    scale,
    palette,
    crs,
  }) {
    if (!bounds && format === 'raw') {
      const file = `${demType}.tif`
      return this.apiBase.getProjectResultUrl(projectId, file, filename)
    } else {
      const params = {
        demType,
        format,
        output_driver: fileFormat,
        scale,
        bounds,
        palette,
      }
      if (crs !== 4326) {
        params.crs = crs
      }
      const results = await this.handleExport(
        projectIdentifier,
        'exports/elevation',
        params,
        filename
      )
      return results[0]
    }
  }

  async plantCounts(projectId, projectIdentifier, path, filename, projectType) {
    return this.apiBase.job
      .fetchJobResult(
        projectIdentifier,
        'plant_detection',
        {
          detectionPath: `uploads/${projectIdentifier}/${path}`,
          removedIds: [],
          mode:
            projectType === 'individual_images'
              ? 'geojsons_to_shps'
              : 'shape_to_zip',
        },
        { filename }
      )
      .then(({ urls: [url] }) => url)
  }

  async analysis({
    projectIdentifier,
    index,
    bands,
    colorMapping,
    filename,
    bounds,
    scale,
    crs,
    fileFormat,
  }) {
    const params = {
      index_name: index,
      bands,
      mapping: colorMapping,
      bounds,
      scale,
      output_driver: fileFormat,
    }
    if (crs !== 4326) {
      params.crs = crs
    }
    const results = await this.handleExport(
      projectIdentifier,
      'vegetation_index',
      params,
      filename
    )
    return results[0]
  }

  async prescription(
    projectIdentifier,
    index,
    scale,
    datasetPath,
    bands,
    thresholds,
    rates,
    colors,
    mask,
    filename,
    options
  ) {
    const params = {
      ...options,
      index,
      scale,
      datasetPath,
      bands,
      thresholds,
      rates,
      colors,
      mask,
    }
    const results = await this.handleExport(
      projectIdentifier,
      'prescription',
      params,
      filename
    )
    return results[0]
  }

  async plantCountZones(
    projectIdentifier,
    plantCountPath,
    zonesGeoJson,
    filename,
    format,
    units
  ) {
    const params = {
      plantCountPath: `uploads/${projectIdentifier}/${plantCountPath}`,
      format,
      units,
    }

    const zonesAttachment = new Blob([JSON.stringify(zonesGeoJson)], {
      type: 'application/json',
    })
    zonesAttachment.filename = 'zones.json'

    const results = await this.handleExport(
      projectIdentifier,
      'plantcount_zones',
      params,
      filename,
      [zonesAttachment]
    )
    return results[0]
  }

  handleExport(
    projectIdentifier,
    operation,
    params,
    filename,
    attachments = []
  ) {
    return this.apiBase.job
      .fetchJobResult(projectIdentifier, operation, params, {
        filename,
        attachments,
      })
      .then(({ urls }) => urls)
  }
}
